import { defineComponent as _defineComponent } from 'vue'
import { setState } from 'ah-common-lib/src/form/helpers';
import { SimpleIndividual, IndividualType, CompanyRegistrationModel, clearIndividual } from 'ah-api-gateways';
import { FormDefinition, FormEvent, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { toDataModel } from 'ah-common-lib/src/form/helpers';
import { cloneDeep } from 'lodash';
import { computed, reactive, watch } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { makeAdditionalUserFM, makeUserTypeFM } from '@/app/helpers/registration/forms';
import ProposedUserPermissionsForm from './ProposedUserPermissionsForm.vue';
import { useAuthStore } from '@/app/store/authStore';
import { AdditionalUserFormObj, useRegistrationIndividuals } from './useRegistrationIndividuals';
import { useTheme } from 'ah-theme';
import { helpers } from '@vuelidate/validators';


export default /*#__PURE__*/_defineComponent({
  __name: 'AccountOwnershipForm',
  props: {
    model: null
  },
  emits: ["proceed", "update:model", "update:validation"],
  setup(__props: any, { emit }: { emit: ({
  /**
   * 'proceed' - Emitted when valid form is submitted
   */
  (e: 'proceed', value: void): void;
  /**
   * 'update:model' - Emitted when any changes on `model` are done
   */
  (e: 'update:model', value: Partial<CompanyRegistrationModel>): void;
  /**
   * 'update:validation' - Emitted on any form validation changes
   */
  (e: 'update:validation', value: FormValidation<CompanyRegistrationModel>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { model: Partial<CompanyRegistrationModel> };

const requestManager = useRequestManager().manager;
const authStore = useAuthStore();
const theme = useTheme();



const differentFromIndividuals = helpers.withParams(
  { type: 'differentFromIndividuals' },
  (val: any) => !registrationIndividuals.isDuplicatedEmail(val)
);

const differentFromApplicant = helpers.withParams(
  { type: 'differentFromApplicant' },
  (val: any) => registrationIndividuals.applicantEmail.value !== val
);

const differentFromOwner = helpers.withParams(
  { type: 'differentFromOwner' },
  (val: any) => registrationIndividuals.ownerEmail.value !== val
);

const differentFromSecondaryOwner = helpers.withParams(
  { type: 'differentFromSecondaryOwner' },
  (val: any) => registrationIndividuals.secondaryOwnerEmail.value !== val
);

function ownerEmailValidations() {
  return { differentFromApplicant, differentFromIndividuals, differentFromSecondaryOwner };
}

function secondaryOwnerEmailValidations() {
  return { differentFromApplicant, differentFromIndividuals, differentFromOwner };
}

function otherEmailValidations() {
  return { differentFromApplicant, differentFromIndividuals, differentFromSecondaryOwner, differentFromOwner };
}

const individuals = computed(
  () =>
    props.model.individuals || [
      {
        ...cloneDeep(authStore.userData?.individual),
        ...cloneDeep(props.model.applicant),
        applicant: true,
        owner: true,
      } as SimpleIndividual,
    ]
);

const registrationIndividuals = useRegistrationIndividuals({
  requestManager,
  individuals,
  userFormFactory: (individual) => {
    if (individual?.owner) {
      return makeAdditionalUserFM(ownerEmailValidations(), true);
    } else if (individual?.secondaryOwner) {
      return makeAdditionalUserFM(secondaryOwnerEmailValidations(), true);
    }
    return makeAdditionalUserFM(otherEmailValidations(), false);
  },
});



const signatoryFM = reactive<FormDefinition>({ form: makeUserTypeFM(), validation: null });

const partnerName = computed(() => theme?.val?.name || '');

function calculateModel(): CompanyRegistrationModel {
  return {
    ...props.model,
    owner: registrationIndividuals.owner.value
      ? clearIndividual({
          ...toDataModel(registrationIndividuals.owner.value.form),
          type: IndividualType.CLIENT_ADMIN,
          owner: true,
        })
      : undefined,
    individuals: registrationIndividuals.individualEntries.value.map((i) => clearIndividual(i.individual)),
  };
}

function onUserFormEvent(event: FormEvent, userForm: AdditionalUserFormObj) {
  if (event.event === 'form-field-set-value') {
    setState(userForm.form, 'errors', [], true);
    registrationIndividuals.updateEntry(
      {
        ...userForm.individual,
        ...toDataModel(userForm.form),
      },
      userForm
    );
  }
}

function submit() {
  registrationIndividuals.saveEntries().subscribe(() => {
    emit('update:model', calculateModel());
    emit('proceed');
  });
}

function onOwnerFormEvent(event: FormEvent) {
  if (event.event === 'form-field-set-value') {
    const applicantIsOwner = signatoryFM.form.userTypes === 'owner';
    if (applicantIsOwner) {
      if (!registrationIndividuals.isApplicantOwner.value && registrationIndividuals.owner.value?.individual.id) {
        registrationIndividuals.removeEntry(registrationIndividuals.owner.value.individual.id);
      }
      registrationIndividuals.updateEntry(
        { owner: true, type: IndividualType.CLIENT_ADMIN },
        registrationIndividuals.applicant.value!
      );
    } else if (registrationIndividuals.isApplicantOwner.value) {
      const owner = props.model.owner || props.model.individuals?.find((i) => i.owner && !i.applicant);
      registrationIndividuals.addEntry({ ...owner, owner: true, applicant: false, type: IndividualType.CLIENT_ADMIN });
      registrationIndividuals.updateEntry(
        { owner: false, type: IndividualType.CLIENT_INDIVIDUAL },
        registrationIndividuals.applicant.value!
      );
    }
  }
}
watch(
  registrationIndividuals.isApplicantOwner,
  () => {
    signatoryFM.form.userTypes = !registrationIndividuals.isApplicantOwner.value ? 'user' : 'owner';
  },
  { immediate: true }
);

watch(
  registrationIndividuals.validation,
  () =>
    emit('update:validation', {
      ...registrationIndividuals.validation.value,
      $model: calculateModel(),
    } as FormValidation<CompanyRegistrationModel>),
  {
    immediate: true,
  }
);

return { __sfc: true,requestManager, authStore, theme, props, differentFromIndividuals, differentFromApplicant, differentFromOwner, differentFromSecondaryOwner, ownerEmailValidations, secondaryOwnerEmailValidations, otherEmailValidations, individuals, registrationIndividuals, emit, signatoryFM, partnerName, calculateModel, onUserFormEvent, submit, onOwnerFormEvent, ProposedUserPermissionsForm }
}

})